import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { getSignedUrl } from '../utils/getSignedUrl';
import { tagIcons, tagColors } from '../constants/tagConfig';
import { useSongContext } from '../SongContext';
import { FaPlay, FaPause, FaDownload } from 'react-icons/fa';
import { generateWaveformData } from '../utils/audioWaveform';

// Add polyfill for requestIdleCallback
const requestIdleCallbackPolyfill = (callback) => {
  const start = Date.now();
  return setTimeout(() => {
    callback({
      didTimeout: false,
      timeRemaining: () => Math.max(0, 50 - (Date.now() - start))
    });
  }, 1);
};

// Use the native version if available, otherwise use the polyfill
const requestIdle = window.requestIdleCallback || requestIdleCallbackPolyfill;

// Global queue for waveform generation
const waveformQueue = [];
let isProcessingQueue = false;
const MAX_CONCURRENT_WAVEFORMS = 3;
let activeWaveformCount = 0;

// Process waveform generation queue
const processWaveformQueue = async () => {
  if (isProcessingQueue || waveformQueue.length === 0 || activeWaveformCount >= MAX_CONCURRENT_WAVEFORMS) {
    return;
  }

  isProcessingQueue = true;

  while (waveformQueue.length > 0 && activeWaveformCount < MAX_CONCURRENT_WAVEFORMS) {
    const { signedUrl, setWaveformData, setIsLoading, markComplete } = waveformQueue[0];
    
    try {
      activeWaveformCount++;
      const waveform = await generateWaveformData(signedUrl);
      setWaveformData(waveform);
    } catch (error) {
      console.error('Error generating waveform:', error);
    } finally {
      activeWaveformCount--;
      setIsLoading(false);
      markComplete();
      waveformQueue.shift();
    }
  }

  isProcessingQueue = false;

  // If there are still items in the queue and we have capacity, continue processing
  if (waveformQueue.length > 0 && activeWaveformCount < MAX_CONCURRENT_WAVEFORMS) {
    setTimeout(processWaveformQueue, 0);
  }
};

// Styled components
const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  gap: 0;
  padding-right: 20px;
  position: relative;

  @media (max-width: 480px) {
    flex-direction: column;
    padding-right: 12px;
    justify-content: center;
    gap: 8px;
  }
`;

const MobileTagWrapper = styled.div`
  display: none;

  @media (max-width: 480px) {
    display: flex;
    padding: 0;
    margin: 0;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
`;

const DesktopSongInfo = styled.div`
  @media (max-width: 480px) {
    display: none;
  }
`;

const ListItem = styled.div`
  display: flex;
  align-items: stretch;
  gap: 20px;
  padding: 0;
  background: white;
  border: 1px solid rgba(46, 46, 46, 0.2);
  border-radius: 12px;
  margin-bottom: 12px;
  transition: transform 0.2s ease, border-color 0.2s ease;
  height: 120px;
  overflow: hidden;
  position: relative;
  width: 100%;
  will-change: transform;
  transform: translateZ(0);
  cursor: pointer;

  &:hover, &[data-playing="true"] {
    border-color: rgba(46, 46, 46, 0.4);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }

  @media (max-width: 768px) {
    height: 100px;
  }

  @media (max-width: 480px) {
    height: 100px;
    gap: 12px;
  }
`;

const AlbumArt = styled.div`
  width: 120px;
  height: 100%;
  background-image: ${props => props.$image ? `url(${props.$image})` : 'none'};
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
  position: relative;
  background-color: #f0f0f0;
  will-change: transform;
  transform: translateZ(0);

  @media (max-width: 768px) {
    width: 100px;
  }

  @media (max-width: 480px) {
    width: 80px;
    position: relative;
  }
`;

const ArtOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.2);
  opacity: 0;
  transition: opacity 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  ${ListItem}:hover &, ${ListItem}[data-playing="true"] & {
    opacity: 1;
  }

  @media (max-width: 480px) {
    opacity: 0.8;
    background: rgba(0, 0, 0, 0.15);
  }
`;

const PlayButton = styled.button`
  background: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #2e2e2e;
  font-size: 14px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    font-size: 12px;
    opacity: 0.9;
    background: rgba(255, 255, 255, 0.9);
    
    ${ListItem}:hover &, ${ListItem}[data-playing="true"] & {
      opacity: 1;
      background: white;
    }
  }
`;

const SongInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 0 0 auto;
  max-width: 300px;
  position: relative;
  z-index: 2;
  justify-content: center;
  height: 100%;

  @media (max-width: 768px) {
    max-width: 200px;
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

const SongInfo = styled.div`
  flex: 0 0 auto;
  min-width: 0;
  max-width: 300px;
  padding: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    max-width: 200px;
  }

  @media (max-width: 480px) {
    max-width: none;
    width: 100%;
  }
`;

const SongTitle = styled.h3`
  margin: 0 0 5px 0;
  font-family: 'Rubik', sans-serif;
  font-size: 1.1em;
  color: #2e2e2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 480px) {
    display: none;
  }
`;

const TagContainer = styled.div`
  display: flex;
  gap: 8px;
  margin: 0;
  padding-right: 20px;

  @media (max-width: 480px) {
    padding: 0;
    margin: 0;
    justify-content: flex-start;
  }
`;

const TagPill = styled(Link)`
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border: 1px solid ${props => props.color || '#000'};
  border-radius: 16px;
  color: ${props => props.color || '#000'};
  font-family: 'Rubik', sans-serif;
  font-size: 0.8em;
  text-decoration: none;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.color || '#000'};
    color: #fff;
  }

  svg {
    margin-right: 4px;
    font-size: 0.9em;
  }

  @media (max-width: 480px) {
    padding: 2px 8px;
    font-size: 0.7em;
    border-radius: 10px;

    svg {
      display: none;
    }
  }
`;

const WaveformContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  padding-left: 20px;
  min-width: 0;
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  perspective: 1000;
  -webkit-perspective: 1000;

  @media (max-width: 480px) {
    padding-left: 0;
    height: 40px;
    flex: 0 0 auto;
  }
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 60px;
  background: #f5f5f5;
  border-radius: 15px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  transform: translateZ(0);
  will-change: transform;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  perspective: 1000;
  -webkit-perspective: 1000;

  @media (max-width: 480px) {
    height: 40px;
    border-radius: 8px;
  }
`;

const ProgressTracker = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  transform: translateZ(0);
  will-change: transform;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  perspective: 1000;
  -webkit-perspective: 1000;
`;

const ProgressFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${props => props.$isDragging ? props.$dragProgress : props.$progress}%;
  background: rgba(46, 46, 46, 0.2);
  transition: ${props => props.$isDragging ? 'none' : 'all 0.5s cubic-bezier(0.4, 0.0, 0.2, 1)'};
  transform-origin: left center;
  will-change: width, transform;
  transform: translateZ(0);
  backface-visibility: hidden;
  perspective: 1000;
  -webkit-backface-visibility: hidden;
  -webkit-perspective: 1000;
  -webkit-transform: translateZ(0);
`;

const WaveformBars = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
`;

const WaveBar = styled.div`
  width: 2px;
  height: ${props => props.$height * 1.5 || 20}%;
  background: ${props => props.$active ? 
    'linear-gradient(to bottom, #2e2e2e 0%, #2e2e2e 100%)' : 
    'linear-gradient(to bottom, rgba(46, 46, 46, 0.7) 0%, rgba(46, 46, 46, 0.3) 100%)'};
  border-radius: 1px;
  transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center bottom;
  will-change: transform, height;
  transform: translateZ(0);

  @media (max-width: 480px) {
    width: 1.5px;
    height: ${props => props.$height * 0.6 || 10}%;
  }
`;

const DownloadButton = styled(Link)`
  display: none;

  @media (max-width: 480px) {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    background: #f5f5f5;
    border-radius: 10px;
    color: #2e2e2e;
    text-decoration: none;
    font-size: 0.7em;
    font-family: 'Rubik', sans-serif;
    transition: all 0.2s ease;
    position: relative;
    z-index: 5;
    
    &:hover {
      background: #e5e5e5;
    }

    svg {
      font-size: 0.9em;
    }
  }
`;

const SongListItem = ({ song, onPlayToggle, isTagCategory, currentCategory }) => {
  const [albumArtUrl, setAlbumArtUrl] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [waveformData, setWaveformData] = useState(null);
  const [isWaveformQueued, setIsWaveformQueued] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragProgress, setDragProgress] = useState(0);
  const [playStartTime, setPlayStartTime] = useState(null);
  const hasTrackedRef = useRef(false);
  const itemRef = useRef(null);
  const progressBarRef = useRef(null);
  const isTransitioning = useRef(false);
  const wasPlayingRef = useRef(false);
  const isSeeking = useRef(false);
  
  const { 
    currentSongId, 
    isPlaying,
    currentProgress,
    loadAudioBuffer,
    playSong,
    pausePlayback,
    seekTo,
    songsLoaded
  } = useSongContext();
  
  const navigate = useNavigate();
  
  // Determine if this specific song is currently playing
  const isThisSongPlaying = currentSongId === song.song_id && isPlaying;
  
  // Find first tag that isn't the current category tag
  const getDisplayTag = useCallback(() => {
    if (!isTagCategory || !currentCategory) {
      // If not a tag category or no currentCategory provided, show first tag
      return song.song_tags[0];
    }
    
    // Format current category to match tag format (first letter uppercase)
    const formattedCategory = currentCategory.charAt(0).toUpperCase() + currentCategory.slice(1);
    
    // Find first tag that isn't the current category
    const otherTag = song.song_tags.find(tag => tag !== formattedCategory);
    
    // If no other tag found, return the first tag (or empty string if no tags)
    return otherTag || song.song_tags[0] || '';
  }, [song.song_tags, isTagCategory, currentCategory]);
  
  // Memoize the display tag for performance
  const displayTag = useMemo(() => getDisplayTag(), [getDisplayTag]);
  
  // Handle intersection observer to detect when item is visible
  useEffect(() => {
    if (!itemRef.current) return;
    const currentItemRef = itemRef.current;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { 
        rootMargin: '100px 0px',
        threshold: 0.1 
      }
    );
    
    observer.observe(currentItemRef);
    
    return () => {
      observer.unobserve(currentItemRef);
    };
  }, []);

  // Load waveform only when item becomes visible
  useEffect(() => {
    const loadWaveform = async () => {
      if (!isVisible || !song.song_file_wm || isWaveformQueued || waveformData) return;

      try {
        setIsWaveformQueued(true);

        // Get signed URL for the audio file
        const signedUrl = await getSignedUrl(song.song_file_wm);
        if (!signedUrl) {
          throw new Error('Failed to get signed URL');
        }

        // Add to waveform generation queue with lower priority
        const priority = 'low';
        waveformQueue.push({
          signedUrl,
          setWaveformData: (data) => {
            // Use our polyfilled requestIdle
            requestIdle(() => {
              setWaveformData(data);
            });
          },
          setIsLoading: () => {}, // Empty function since we removed isLoading state
          markComplete: () => {
            // Use our polyfilled requestIdle
            requestIdle(() => {
              setIsWaveformQueued(false);
            });
          },
          priority
        });

        // Process queue in background
        requestIdle(() => processWaveformQueue());

        // Load the audio for playback if not already loaded and this is the current song
        if (!songsLoaded[song.song_id] && currentSongId === song.song_id) {
          await loadAudioBuffer(song.song_id, song.song_file_wm);
        }
      } catch (error) {
        console.error('Error queueing waveform generation:', error);
        setIsWaveformQueued(false);
      }
    };

    loadWaveform();
  }, [isVisible, song.song_file_wm, song.song_id, currentSongId, songsLoaded, isWaveformQueued, waveformData, loadAudioBuffer]);

  // Use real waveform data if available, otherwise generate placeholder
  const displayWaveform = useMemo(() => {
    if (waveformData) {
      // The generateWaveformData output is already in a good range (20-90 after its own normalization)
      // Let's map this to our desired display bar height (e.g. 2-65, similar to placeholder)
      const minDisplayHeight = 2;
      const maxDisplayHeight = 65;
      const displayRange = maxDisplayHeight - minDisplayHeight;

      const originalMin = 20; // Expected min from generateWaveformData's normalization
      const originalMax = 90; // Expected max from generateWaveformData's normalization
      const originalRange = originalMax - originalMin;

      return {
        data: waveformData.map(value => {
          // Clamp value to the expected original range first
          const clampedValue = Math.max(originalMin, Math.min(originalMax, value));
          // Normalize from original range (0-1)
          let normalizedOriginal = 0;
          if (originalRange > 0) { // Avoid division by zero if originalRange is 0
            normalizedOriginal = (clampedValue - originalMin) / originalRange;
          }
          // Scale to new display range
          return Math.round(minDisplayHeight + normalizedOriginal * displayRange);
        }),
        isReal: true
      };
    }
    
    // Fallback to placeholder waveform if real data isn't loaded yet
    const barsCount = 48;
    const bars = new Array(barsCount);
    for (let i = 0; i < barsCount; i++) {
      const baseHeight = Math.sin((i / barsCount) * Math.PI * 4) * 25 + 30;
      const randomVariation = Math.random() * 8 - 4;
      const height = Math.max(2, Math.min(65, baseHeight + randomVariation));
      bars[i] = Math.round(height);
    }
    
    // Smooth placeholder data
    for (let i = 1; i < barsCount - 1; i++) {
      bars[i] = Math.round((bars[i - 1] + bars[i] * 2 + bars[i + 1]) / 4);
    }
    
    return {
      data: bars,
      isReal: false
    };
  }, [waveformData]);

  // Load album art when component mounts - with debouncing
  useEffect(() => {
    let mounted = true;
    
    const loadArt = async () => {
      try {
        if (song.song_art && mounted) {
          // Create new Image object to preload
          const img = new Image();
          const signedUrl = await getSignedUrl(song.song_art);
          
          if (signedUrl && mounted) {
            // Only set URL after image has loaded
            img.onload = () => {
              if (mounted) {
                setAlbumArtUrl(signedUrl);
              }
            };
            img.src = signedUrl;
          }
        }
      } catch (error) {
        console.error('Error loading album art:', error);
      }
    };

    // Only load art if component is visible
    if (isVisible) {
      loadArt();
    }

    return () => {
      mounted = false;
    };
  }, [song.song_art, isVisible]);

  // Add trackPlay function
  const trackPlay = async (songId, playDuration) => {
    console.log('Tracking play:', { songId, playDuration });
    
    try {
      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_API_BASE_URL}/songs/${songId}/play`;

      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ playDuration })
      });

      if (!response.ok) {
        console.error('Play tracking failed:', {
          status: response.status,
          statusText: response.statusText
        });
        return;
      }

      await response.json();
      console.log('Play tracked successfully for song:', songId);
    } catch (error) {
      console.error('Error tracking play:', {
        error: error.message,
        songId,
        playDuration
      });
    }
  };

  // Calculate drag percentage based on mouse/touch position
  const calculateProgressFromEvent = useCallback((e) => {
    if (!progressBarRef.current) return 0;
    
    const rect = progressBarRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const percentage = ((clientX - rect.left) / rect.width) * 100;
    const boundedPercentage = Math.max(0, Math.min(100, percentage));
    
    console.log('[waveform] calculateProgressFromEvent:', {
      clientX,
      rectLeft: rect.left,
      rectWidth: rect.width,
      rawPercentage: percentage,
      boundedPercentage,
    });
    
    return boundedPercentage;
  }, []);

  // Handle play/pause toggle - MOVED UP so it's defined before functions that reference it
  const handlePlayToggle = useCallback(async (percentage) => {
    if (!song.song_id || !song.song_file_wm || isTransitioning.current) return;
    
    console.log('[playToggle] Called with percentage:', percentage, 'songId:', song.song_id, 'isPlaying:', isThisSongPlaying);
    
    try {
      isTransitioning.current = true;
      
      if (isThisSongPlaying) {
        // This song is currently playing - pause it
        console.log('[playToggle] Pausing current playback');
        pausePlayback();
        
        // Track play duration when pausing
        if (playStartTime && !hasTrackedRef.current) {
          const playDuration = Math.floor((Date.now() - playStartTime) / 1000);
          if (playDuration >= 5) {
            hasTrackedRef.current = true;
            trackPlay(song.song_id, playDuration);
          }
          setPlayStartTime(null);
        }
        
        if (typeof percentage === 'number' && !isSeeking.current) {
          console.log('[playToggle] Seeking during pause to:', percentage);
          isSeeking.current = true;
          seekTo(percentage);
          setTimeout(() => {
            isSeeking.current = false;
          }, 100);
        }
      } else {
        // Different song or nothing playing - start this one
        if (!songsLoaded[song.song_id]) {
          console.log('[playToggle] Loading audio buffer for songId:', song.song_id);
          setIsWaveformQueued(true);
          await loadAudioBuffer(song.song_id, song.song_file_wm);
          setIsWaveformQueued(false);
        }
        
        console.log('[playToggle] Starting playback at position:', percentage || 0);
        const success = await playSong(song.song_id, song.song_file_wm, percentage || 0);
        
        if (success) {
          console.log('[playToggle] Playback started successfully');
          setPlayStartTime(Date.now());
          hasTrackedRef.current = false;
        } else {
          console.error('[playToggle] Failed to play song');
        }
      }
      
      if (onPlayToggle) {
        onPlayToggle(!isThisSongPlaying);
      }
    } catch (error) {
      console.error('[playToggle] Error toggling playback:', error);
    } finally {
      isTransitioning.current = false;
    }
  }, [song.song_id, song.song_file_wm, isThisSongPlaying, pausePlayback, playSong, loadAudioBuffer, songsLoaded, seekTo, onPlayToggle, playStartTime]);

  // Handle waveform interaction start (mousedown/touchstart)
  const handleWaveformInteractionStart = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!song.song_id || !song.song_file_wm || isTransitioning.current) return;
    
    console.log('[waveform] Interaction start - songId:', song.song_id, 'isPlaying:', isThisSongPlaying);
    
    setIsDragging(true);
    wasPlayingRef.current = isThisSongPlaying;
    
    const percentage = calculateProgressFromEvent(e);
    setDragProgress(percentage);
    
    console.log('[waveform] Interaction start - percentage:', percentage, 'wasPlaying:', wasPlayingRef.current);
    
    if (isThisSongPlaying) {
      // If already playing, just seek
      console.log('[waveform] Seeking during playback:', percentage);
      isSeeking.current = true;
      seekTo(percentage);
    } else {
      // If not playing, start playing from this position immediately
      // This gives a more responsive feel than waiting for the end of the interaction
      console.log('[waveform] Starting playback immediately from position:', percentage);
      if (!songsLoaded[song.song_id]) {
        await loadAudioBuffer(song.song_id, song.song_file_wm);
      }
      handlePlayToggle(percentage);
    }
  }, [song.song_id, song.song_file_wm, isThisSongPlaying, calculateProgressFromEvent, seekTo, songsLoaded, loadAudioBuffer, handlePlayToggle]);

  // Handle waveform interaction move (mousemove/touchmove)
  const handleWaveformInteractionMove = useCallback((e) => {
    if (!isDragging) return;
    
    e.preventDefault();
    const percentage = calculateProgressFromEvent(e);
    
    // Use requestAnimationFrame for smoother updates
    requestAnimationFrame(() => {
      setDragProgress(percentage);
      
      if (isThisSongPlaying) {
        isSeeking.current = true;
        seekTo(percentage);
      }
    });
  }, [isDragging, isThisSongPlaying, calculateProgressFromEvent, seekTo]);

  // Handle waveform interaction end (mouseup/touchend)
  const handleWaveformInteractionEnd = useCallback(async () => {
    if (!isDragging) return;
    
    console.log('[waveform] Interaction end - wasPlaying:', wasPlayingRef.current, 'isPlaying:', isThisSongPlaying, 'percentage:', dragProgress);
    
    try {
      if (!wasPlayingRef.current && !isThisSongPlaying) {
        // If it wasn't playing when we started dragging, start playing from the dragged position
        console.log('[waveform] Starting playback at position:', dragProgress);
        await handlePlayToggle(dragProgress);
      } else if (isThisSongPlaying) {
        // Final seek if the song was already playing
        console.log('[waveform] Final seek to:', dragProgress);
        isSeeking.current = true;
        seekTo(dragProgress);
      }
    } finally {
      setIsDragging(false);
      wasPlayingRef.current = false;
      // Reset seeking flag after a short delay to allow the final seek to complete
      setTimeout(() => {
        isSeeking.current = false;
      }, 100);
    }
  }, [isDragging, dragProgress, handlePlayToggle, isThisSongPlaying, seekTo]);

  // Add effect to track plays when component unmounts or song changes
  useEffect(() => {
    // Track play on unmount or song change
    const checkAndTrackPlay = () => {
      if (playStartTime && !hasTrackedRef.current) {
        const playDuration = Math.floor((Date.now() - playStartTime) / 1000);
        if (playDuration >= 5) {
          hasTrackedRef.current = true;
          trackPlay(song.song_id, playDuration);
        }
        setPlayStartTime(null);
      }
    };

    // Add beforeunload event listener to track plays when navigating away
    const handleBeforeUnload = () => {
      if (isThisSongPlaying) {
        checkAndTrackPlay();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup function
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (isThisSongPlaying) {
        checkAndTrackPlay();
      }
    };
  }, [isThisSongPlaying, playStartTime, song.song_id]);

  // Add and remove window event listeners for drag interactions
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleWaveformInteractionMove);
      window.addEventListener('mouseup', handleWaveformInteractionEnd);
      window.addEventListener('touchmove', handleWaveformInteractionMove);
      window.addEventListener('touchend', handleWaveformInteractionEnd);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleWaveformInteractionMove);
      window.removeEventListener('mouseup', handleWaveformInteractionEnd);
      window.removeEventListener('touchmove', handleWaveformInteractionMove);
      window.removeEventListener('touchend', handleWaveformInteractionEnd);
    };
  }, [isDragging, handleWaveformInteractionMove, handleWaveformInteractionEnd]);

  // Calculate which waveform bars should be active based on progress - memoized
  const activeBarIndex = useMemo(() => {
    if (!isThisSongPlaying) return -1;
    return Math.floor((displayWaveform.data.length * currentProgress) / 100);
  }, [isThisSongPlaying, currentProgress, displayWaveform.data.length]);

  const handleItemClick = useCallback((e) => {
    // Navigate to song page only if clicking on the main area
    // and not on interactive elements
    navigate(`/songs/${song.song_id}`);
  }, [navigate, song.song_id]);

  const handleInteractiveClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  return (
    <ListItem 
      ref={itemRef} 
      $isPlaying={isThisSongPlaying} 
      data-playing={isThisSongPlaying}
      onClick={handleItemClick}
    >
      <AlbumArt $image={albumArtUrl} onClick={handleInteractiveClick}>
        <ArtOverlay>
          <PlayButton onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handlePlayToggle();
          }}>
            {isThisSongPlaying ? <FaPause /> : <FaPlay />}
          </PlayButton>
        </ArtOverlay>
      </AlbumArt>
      <ContentWrapper>
        <DesktopSongInfo>
          <SongInfoWrapper>
            <SongInfo>
              <SongTitle>{song.song_title}</SongTitle>
            </SongInfo>
            <TagContainer onClick={handleInteractiveClick}>
              {displayTag && (
                <TagPill
                  key={displayTag}
                  to={`/${displayTag.toLowerCase()}`}
                  color={tagColors[displayTag] || '#000'}
                  onClick={(e) => e.stopPropagation()}
                >
                  {tagIcons[displayTag] && React.createElement(tagIcons[displayTag])}
                  {displayTag}
                </TagPill>
              )}
            </TagContainer>
          </SongInfoWrapper>
        </DesktopSongInfo>
        <WaveformContainer>
          <ProgressBar 
            ref={progressBarRef}
            onMouseDown={handleWaveformInteractionStart}
            onTouchStart={handleWaveformInteractionStart}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <ProgressTracker>
              <ProgressFill 
                $progress={isThisSongPlaying ? currentProgress : 0}
                $isDragging={isDragging}
                $dragProgress={dragProgress}
              />
            </ProgressTracker>
            <WaveformBars>
              {displayWaveform.data.map((height, index) => (
                <WaveBar 
                  key={index} 
                  $height={height}
                  $index={index}
                  $isReal={displayWaveform.isReal}
                  $active={isThisSongPlaying && index <= (isDragging ? 
                    Math.floor((displayWaveform.data.length * dragProgress) / 100) : 
                    activeBarIndex)}
                />
              ))}
            </WaveformBars>
          </ProgressBar>
        </WaveformContainer>
        <MobileTagWrapper onClick={handleInteractiveClick}>
          <div>
            {displayTag && (
              <TagPill
                key={displayTag}
                to={`/${displayTag.toLowerCase()}`}
                color={tagColors[displayTag] || '#000'}
                onClick={(e) => e.stopPropagation()}
              >
                {tagIcons[displayTag] && React.createElement(tagIcons[displayTag])}
                {displayTag}
              </TagPill>
            )}
          </div>
          <DownloadButton 
            to={`/songs/${song.song_id}`}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <FaDownload /> Download
          </DownloadButton>
        </MobileTagWrapper>
      </ContentWrapper>
    </ListItem>
  );
};

export default SongListItem; 